import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { useEffect, useState, useMemo } from "react";

import { useSearchParams } from "next/navigation";

import {
  Text,
  Heading,
  Box,
  HTMLWrapper,
  Button,
  Spinner,
  VStack,
  Center
} from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import ResendVerificationCode from "./resend-verification-code";
import VerificationForm from "./verification-form";

import { resetAnswers } from "@/redux/features/questions/slice";
import { resetRegistrations } from "@/redux/features/registrations/slice";
import { stripAllHtml } from "@/lib";
import Alerts from "@/components/alerts";
import isRelativePath from "@/helpers/is-relative-path";
import { parseNextUrlFromQuery } from "@/helpers/parse-next-url";
import { extractQueryParams } from "@/helpers/extract-query-params";
import { handleEmailConfirmation } from "@/redux/features/user-confirmation/thunk";
import { fetchAnonymousUser } from "@/redux/features/anonymous-user/thunk";

const paramMappings = {
  type: "type",
  key: "key",
  token: "token"
};

const VerifyAccount = (): JSX.Element => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const searchParams = useSearchParams();

  const { isOtpVerifiedSuccessfully, isEmailConfirmed, error } = useAppSelector(
    (state) => state.userConfirmation
  );
  const { registrations } = useAppSelector((state) => state.registrations);
  const { confirmed: isUserConfirmed } = registrations;
  const { isLoggedIn } = useAppSelector((state) => state.user.registeredUser);
  const { welcomePage } = useAppSelector(
    (state) => state.messageText.messageText
  );

  const nextPageUrl = parseNextUrlFromQuery(router.query);
  const params = useMemo(
    () => extractQueryParams(searchParams, paramMappings),
    [searchParams]
  );

  const [emailVerificationInProgress, setEmailVerificationInProgress] =
    useState(params.type === "email_confirm");

  // Redirect user after login
  useEffect(() => {
    if (isLoggedIn) {
      if (nextPageUrl && isRelativePath(nextPageUrl)) router.push(nextPageUrl);
      else router.push("/");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (
      params.type === "email_confirm" &&
      !isLoggedIn &&
      emailVerificationInProgress
    ) {
      const confirmEmail = async () => {
        await dispatch(fetchAnonymousUser({}));

        await dispatch(
          handleEmailConfirmation({
            encryptedUserId: params.key,
            confirmationToken: params.token
          })
        );
        setEmailVerificationInProgress(false);
      };

      confirmEmail();
    }
  }, [
    params.type,
    params.key,
    params.token,
    isLoggedIn,
    dispatch,
    emailVerificationInProgress
  ]);

  const handleStartAgain = () => {
    router.replace("/register");
    dispatch(resetRegistrations());
    dispatch(resetAnswers());
  };

  // Show verification in progress for email confirmation
  if (params.type === "email_confirm" && emailVerificationInProgress) {
    return (
      <Center minH="300px">
        <VStack gap={6}>
          <Spinner
            size="xl"
            speed="0.65s"
            thickness="4px"
            emptyColor="gray.200"
          />
          <VStack gap={2}>
            <Heading as="h2" size="lg" textAlign="center">
              {translate("register.verifyingEmail")}
            </Heading>
            <Text color="gray.600" textAlign="center">
              {translate("common.submitting")}...
            </Text>
          </VStack>
        </VStack>
      </Center>
    );
  }

  // Show error after verification completes
  if (
    params.type === "email_confirm" &&
    !emailVerificationInProgress &&
    error
  ) {
    return (
      <Center minH="300px">
        <Box maxW="500px" w="100%">
          <VStack gap={6} align="stretch">
            <Alerts
              status="error"
              title={translate("register.verificationFailed")}
              description={translate("register.unprocessableEntity")}
              borderRadius="md"
              p={6}
            />
            <VStack gap={3}>
              <Text color="gray.600" textAlign="center" fontSize="sm">
                {translate("register.emailLinkHelpText")}
              </Text>
              <Button
                variant="primary"
                width="100%"
                onClick={() => router.push("/register")}
                data-testid="emailVerification-returnButton"
              >
                {translate("register.returnToRegistration")}
              </Button>
            </VStack>
          </VStack>
        </Box>
      </Center>
    );
  }

  const isWelcomeMessagePresent = (txt) => {
    return stripAllHtml(txt).length > 0;
  };

  if (isOtpVerifiedSuccessfully || isUserConfirmed || isEmailConfirmed) {
    const isWelcomeMsg = isWelcomeMessagePresent(welcomePage);
    return (
      <Box textAlign="center">
        {isWelcomeMsg ? (
          <HTMLWrapper my={4} html={welcomePage as string} />
        ) : (
          <Alerts
            flexDir={{ base: "column" }}
            status="success"
            title={translate("register.congratulations", {
              login: registrations.login
            })}
            description={translate("register.accountCreated")}
          />
        )}
        <Button
          variant="primary"
          width={{ md: "auto", sm: "100%" }}
          mt={{ md: 12, sm: 6 }}
          onClick={() =>
            nextPageUrl
              ? router.push(`/login?next=${encodeURIComponent(nextPageUrl)}`)
              : router.push("/login")
          }
          alignSelf={{ base: "center", sm: "flex-start" }}
          data-testid="accountVerification-continueButton"
        >
          {translate("common.continue")}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Heading as="h1" my={3} data-testid="accountVerification-heading">
        {translate("register.verifyAccount")}
      </Heading>
      <VerificationForm />
      <ResendVerificationCode />
      <Text my="2">
        {translate("register.wrongEmail")} &nbsp;
        <Button
          variant="link"
          onClick={handleStartAgain}
          data-testid="accountVerification-startOverButton"
        >
          {translate("register.startOver")}
        </Button>
      </Text>
    </>
  );
};

export default VerifyAccount;
